export const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export const STATUSES = [
  { value: "not_started", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Done" },
];

export const CREATE_STATUSES = [
  { value: "not_started", label: "To do" },
  { value: "in_progress", label: "In progress" },
];

export const AREAS = ["Frontend", "Backend", "Frontend + Backend"];

export const getTodayDateString = () => new Date().toISOString().slice(0, 10);

export const priorityBadgeStyle = (priority = "medium", isDark = false) => {
  const norm = String(priority || "medium").toLowerCase();
  switch (norm) {
    case "urgent":
      return isDark ? "bg-rose-500/20 text-rose-300 border-rose-500/40" : "bg-rose-100 text-rose-800 border-rose-200";
    case "high":
      return isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-amber-100 text-amber-800 border-amber-200";
    case "low":
      return isDark ? "bg-slate-700/60 text-slate-300 border-slate-600" : "bg-slate-100 text-slate-700 border-slate-200";
    case "medium":
    default:
      return isDark ? "bg-sky-500/20 text-sky-300 border-sky-500/40" : "bg-sky-100 text-sky-800 border-sky-200";
  }
};

/**
 * Validates date constraints for DevActivity:
 * - Create accepts only not_started or in_progress; completion is available through update, where actualEnd is required.
 * - Planned start and due dates must be today or later.
 * - Actual start cannot be before today, and actual end cannot be after today.
 * - Existing activities with historical planned dates can still be updated unless those dates are changed.
 *
 * @param {object} draft
 * @param {boolean} isCreate
 * @param {object} originalActivity
 * @returns {string|null} validation error message, or null if valid
 */
export const validateActivityDates = (draft = {}, isCreate = false, originalActivity = {}) => {
  const today = getTodayDateString();

  if (isCreate && draft.status === "completed") {
    return "New activities cannot be created as completed. Please select 'To do' or 'In progress'.";
  }

  // Planned Start Date
  if (draft.plannedStart) {
    const isNewOrChanged = isCreate || draft.plannedStart !== originalActivity.plannedStart;
    if (isNewOrChanged && draft.plannedStart < today) {
      return "Planned start date must be today or later.";
    }
  }

  // Due Date (Planned End)
  if (draft.plannedEnd) {
    const isNewOrChanged = isCreate || draft.plannedEnd !== originalActivity.plannedEnd;
    if (isNewOrChanged && draft.plannedEnd < today) {
      return "Due date must be today or later.";
    }
  }

  // Chronological order: plannedEnd >= plannedStart
  if (draft.plannedStart && draft.plannedEnd && draft.plannedEnd < draft.plannedStart) {
    return "Due date cannot be earlier than planned start date.";
  }

  // Actual Start Date
  if (draft.actualStart) {
    const isNewOrChanged = isCreate || draft.actualStart !== originalActivity.actualStart;
    if (isNewOrChanged && draft.actualStart < today) {
      return "Actual start date cannot be before today.";
    }
  }

  // Completed status & Actual End Date
  if (draft.status === "completed") {
    if (!draft.actualEnd) {
      return "Completed date is required when status is completed.";
    }
    if (draft.actualEnd > today) {
      return "Completed date cannot be after today.";
    }
    if (draft.actualStart && draft.actualEnd < draft.actualStart) {
      return "Completed date cannot be earlier than actual start date.";
    }
  } else if (draft.actualEnd && draft.actualEnd > today) {
    return "Completed date cannot be after today.";
  }

  return null;
};
